import * as React from 'react';
import Papa from 'papaparse';
import { getSuperAdminSchools } from 'Src/helpers/service/superadmin/schools';
import { superAdminPost } from 'Src/helpers/service/superadmin/api';

interface ImportRow {
  parentUserId: string;
  studentUserId: string;
  schoolId?: string;
  priority: string | number;
  rowNumber: number;
  parentEmail?: string;
  parentFirstName?: string;
  parentLastName?: string;
  studentFirstName?: string;
  studentLastName?: string;
  studentDOB?: string;
  relationship?: string;
  source?: string;
  parentName?: string;
  studentName?: string;
  resolvedParentEmail?: string;
  resolvedStudentDOB?: string;
  status?: string;
  message?: string;
  permissionId?: string;
  notificationStatus?: string;
  warnings?: string[];
  include?: boolean;
  existingParentPermissions?: Array<{
    permissionId: string;
    studentId: string;
    studentName: string;
    studentDOB?: string;
    priority?: number;
  }>;
  [key: string]: any;
}

interface State {
  schoolSearch: string;
  schoolId: string;
  schoolOptions: Array<{ id: string; name: string }>;
  rows: ImportRow[];
  fileName: string;
  errors: string[];
  isWorking: boolean;
  hasApplied: boolean;
  allowedRelationships: string[];
}

const STATUS_CLASS: { [key: string]: string } = {
  READY: 'badge badge-primary',
  CREATED: 'badge badge-success',
  ALREADY_EXISTS: 'badge badge-secondary',
  SKIPPED_BY_USER: 'badge badge-secondary',
  FAILED: 'badge badge-danger'
};

function normalizeCsvHeader(header: string): string {
  const trimmed = header.replace(/^\ufeff/, '').trim();
  const normalized = trimmed.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (
    normalized === 'relationship' ||
    normalized === 'relation' ||
    normalized === 'relations' ||
    normalized === 'parentrelationship' ||
    normalized === 'relationshiptostudent' ||
    normalized === 'relationtostudent'
  ) {
    return 'relationship';
  }
  return trimmed;
}

function downloadResults(rows: ImportRow[]) {
  const columns = rows.map(row => ({
    parentUserId: row.parentUserId,
    parentEmail: row.parentEmail || '',
    parentFirstName: row.parentFirstName || '',
    parentLastName: row.parentLastName || '',
    resolvedParentName: row.parentName || '',
    resolvedParentEmail: row.resolvedParentEmail || '',
    studentUserId: row.studentUserId,
    studentFirstName: row.studentFirstName || '',
    studentLastName: row.studentLastName || '',
    studentDOB: row.studentDOB || '',
    resolvedStudentName: row.studentName || '',
    resolvedStudentDOB: row.resolvedStudentDOB || '',
    schoolId: row.schoolId || '',
    priority: row.priority,
    relationship: row.relationship || '',
    source: row.source || '',
    result: row.status || '',
    message: row.message || '',
    permissionId: row.permissionId || '',
    notificationStatus: row.notificationStatus || '',
    warnings: (row.warnings || []).join(' | '),
    existingParentPermissions: (row.existingParentPermissions || [])
      .map(permission => `${permission.studentName} (${permission.studentId})`)
      .join(' | '),
    selectedForImport: row.include === true ? 'Yes' : 'No'
  }));
  const blob = new Blob(['\ufeff' + Papa.unparse(columns)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'parent_permissions_result.csv';
  anchor.click();
  URL.revokeObjectURL(url);
}

export class SuperAdminParentPermissions extends React.Component<{}, State> {
  schoolSearchTimer: any;

  state: State = {
    schoolSearch: '',
    schoolId: '',
    schoolOptions: [],
    rows: [],
    fileName: '',
    errors: [],
    isWorking: false,
    hasApplied: false,
    allowedRelationships: []
  };

  onSchoolSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const schoolSearch = event.target.value;
    this.setState({
      schoolSearch,
      schoolId: '',
      schoolOptions: [],
      rows: [],
      errors: [],
      hasApplied: false,
      allowedRelationships: []
    });
    clearTimeout(this.schoolSearchTimer);
    if (schoolSearch.trim().length < 2) return;
    this.schoolSearchTimer = setTimeout(() => {
      getSuperAdminSchools({ where: { name: { like: schoolSearch, options: 'i' } }, limit: 10, skip: 0 })
        .then(schools =>
          this.setState({ schoolOptions: schools.map(school => ({ id: school.id, name: school.name || '' })) })
        )
        .catch(() => this.setState({ errors: ['Could not search schools.'] }));
    }, 300);
  };

  selectSchool = (school: { id: string; name: string }) => {
    this.setState({
      schoolId: school.id,
      schoolSearch: school.name,
      schoolOptions: [],
      rows: [],
      errors: [],
      hasApplied: false,
      allowedRelationships: []
    });
  };

  onFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: normalizeCsvHeader,
      complete: result => {
        const fields = result.meta.fields || [];
        const missing = ['parentUserId', 'studentUserId', 'priority'].filter(field => fields.indexOf(field) === -1);
        if (missing.length) {
          this.setState({
            rows: [],
            fileName: file.name,
            errors: [`Missing required column(s): ${missing.join(', ')}`]
          });
          return;
        }
        const rows = (result.data as any[]).map((row, index) => ({
          ...row,
          relationship: row.relationship || row.source || '',
          rowNumber: index + 2
        }));
        const errors = result.errors.map(error => `Row ${(error.row || 0) + 2}: ${error.message}`);
        this.setState({ rows, fileName: file.name, errors, hasApplied: false }, () => {
          if (!errors.length) this.preview();
        });
      }
    });
  };

  request = (path: 'preview' | 'apply') => {
    const { schoolId, rows } = this.state;
    if (!schoolId || !rows.length) return;
    this.setState({ isWorking: true, errors: [] });
    superAdminPost<{ rows: ImportRow[]; allowedRelationships: string[] }>(`/tools/parent-permissions/${path}`, {
      schoolId,
      rows
    })
      .then(response =>
        this.setState({
          rows: response.rows.map(row => ({
            ...row,
            include: path === 'preview' ? row.status === 'READY' : row.include
          })),
          allowedRelationships: response.allowedRelationships || [],
          isWorking: false,
          hasApplied: path === 'apply'
        })
      )
      .catch(error => {
        const message =
          error && error.response && error.response.data && error.response.data.details
            ? error.response.data.details.text
            : `Could not ${path} parent permissions.`;
        this.setState({ isWorking: false, errors: [message] });
      });
  };

  preview = () => this.request('preview');
  apply = () => this.request('apply');

  setRowIncluded = (rowIndex: number, include: boolean) => {
    this.setState(state => ({
      rows: state.rows.map((row, index) => (index === rowIndex ? { ...row, include } : row))
    }));
  };

  setAllReadyIncluded = (include: boolean) => {
    this.setState(state => ({ rows: state.rows.map(row => (row.status === 'READY' ? { ...row, include } : row)) }));
  };

  render() {
    const {
      schoolSearch,
      schoolId,
      schoolOptions,
      rows,
      fileName,
      errors,
      isWorking,
      hasApplied,
      allowedRelationships
    } = this.state;
    const counts = rows.reduce((map: { [key: string]: number }, row) => {
      const status = row.status || 'UPLOADED';
      map[status] = (map[status] || 0) + 1;
      return map;
    }, {});
    const ready = counts.READY || 0;
    const selectedReady = rows.filter(row => row.status === 'READY' && row.include !== false).length;
    const warningCount = rows.filter(row => row.warnings && row.warnings.length > 0).length;
    return (
      <div>
        <div className="card mb-4">
          <div className="card-body">
            <h4>Update parent permissions</h4>
            <p className="text-muted">
              Upload reconciliation results, review every row, then create only permissions which pass server
              validation.
            </p>
            <div className="row">
              <div className="col-md-6 position-relative">
                <label className="font-weight-bold">School</label>
                <input
                  className="form-control"
                  value={schoolSearch}
                  onChange={this.onSchoolSearch}
                  placeholder="Start typing a school name"
                />
                {schoolOptions.length > 0 && (
                  <div className="list-group position-absolute w-100" style={{ zIndex: 10 }}>
                    {schoolOptions.map(school => (
                      <button
                        type="button"
                        className="list-group-item list-group-item-action"
                        key={school.id}
                        onClick={() => this.selectSchool(school)}
                      >
                        {school.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <label className="font-weight-bold">CSV file</label>
                <input
                  type="file"
                  className="form-control-file"
                  accept=".csv,text/csv"
                  disabled={!schoolId || isWorking}
                  onChange={this.onFile}
                />
                {fileName && <small className="text-muted">{fileName}</small>}
              </div>
            </div>
          </div>
        </div>

        {errors.length > 0 && (
          <div className="alert alert-danger">
            {errors.map(error => (
              <div key={error}>{error}</div>
            ))}
          </div>
        )}

        {rows.length > 0 && (
          <>
            <div className="card mb-3">
              <div className="card-body py-3">
                <div className="font-weight-bold mb-2">Relationships allowed for parent auto-accept</div>
                {allowedRelationships.length > 0 ? (
                  allowedRelationships.map(relationship => (
                    <span className="badge badge-light border mr-2 mb-1" key={relationship}>
                      {relationship}
                    </span>
                  ))
                ) : (
                  <span className="text-muted">No relationships are configured for auto-accept at this school.</span>
                )}
              </div>
            </div>
            <div className="d-flex flex-wrap align-items-center mb-3">
              <strong className="mr-3">{rows.length} rows uploaded</strong>
              {Object.keys(counts).map(status => (
                <span className="badge badge-light border mr-2" key={status}>
                  {status.replace(/_/g, ' ')}: {counts[status]}
                </span>
              ))}
              {warningCount > 0 && <span className="badge badge-warning mr-2">Warnings: {warningCount}</span>}
              <div className="ml-auto">
                <button type="button" className="btn btn-outline-secondary mr-2" onClick={() => downloadResults(rows)}>
                  Download result CSV
                </button>
                {!hasApplied && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!selectedReady || isWorking}
                    onClick={this.apply}
                  >
                    {isWorking ? 'Working…' : `Create ${selectedReady} selected permissions`}
                  </button>
                )}
              </div>
            </div>
            {!hasApplied && ready > 0 && (
              <div className="mb-3">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary mr-2"
                  onClick={() => this.setAllReadyIncluded(true)}
                >
                  Select all ready
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => this.setAllReadyIncluded(false)}
                >
                  Clear selection
                </button>
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>Use</th>
                    <th>Row</th>
                    <th>Parent</th>
                    <th>Student to add</th>
                    <th>Relationship</th>
                    <th>Existing parent links</th>
                    <th>Details</th>
                    <th>Priority</th>
                    <th>Validation / result</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      className={row.warnings && row.warnings.length ? 'table-warning' : ''}
                      key={`${row.rowNumber}-${row.parentUserId}-${row.studentUserId}-${index}`}
                    >
                      <td>
                        {!hasApplied && row.status === 'READY' ? (
                          <input
                            type="checkbox"
                            checked={row.include !== false}
                            aria-label={`Include row ${row.rowNumber}`}
                            onChange={event => this.setRowIncluded(index, event.target.checked)}
                          />
                        ) : row.status === 'SKIPPED_BY_USER' ? (
                          'No'
                        ) : (
                          ''
                        )}
                      </td>
                      <td>{row.rowNumber}</td>
                      <td>
                        <div>
                          {row.parentName ||
                            [row.parentFirstName, row.parentLastName].filter(Boolean).join(' ') ||
                            row.parentEmail ||
                            'Unknown parent'}
                        </div>
                        {(row.resolvedParentEmail || row.parentEmail) && (
                          <small className="d-block text-muted">{row.resolvedParentEmail || row.parentEmail}</small>
                        )}
                        <small className="d-block text-muted">{row.parentUserId}</small>
                      </td>
                      <td>
                        <div>
                          {row.studentName ||
                            [row.studentFirstName, row.studentLastName].filter(Boolean).join(' ') ||
                            'Unknown student'}
                        </div>
                        {(row.resolvedStudentDOB || row.studentDOB) && (
                          <small className="d-block text-muted">DOB: {row.resolvedStudentDOB || row.studentDOB}</small>
                        )}
                        <small className="d-block text-muted">{row.studentUserId}</small>
                      </td>
                      <td>
                        {row.relationship ? (
                          <span className="badge badge-light border">{row.relationship}</span>
                        ) : (
                          <span className="text-muted">Not provided</span>
                        )}
                      </td>
                      <td>
                        {(row.existingParentPermissions || []).length > 0 ? (
                          (row.existingParentPermissions || []).map(permission => (
                            <div className="mb-1" key={permission.permissionId}>
                              <div>{permission.studentName}</div>
                              <small className="d-block text-muted">
                                {permission.studentDOB ? `DOB: ${permission.studentDOB} · ` : ''}Priority:{' '}
                                {typeof permission.priority === 'number' ? permission.priority : 'Not set'}
                              </small>
                              <small className="d-block text-muted">{permission.studentId}</small>
                            </div>
                          ))
                        ) : (
                          <span className="text-muted">None in this school</span>
                        )}
                      </td>
                      <td>
                        {row.source && row.source !== row.relationship && (
                          <small className="d-block text-muted">Source: {row.source}</small>
                        )}
                        <small className="d-block text-muted">School: {row.schoolId}</small>
                      </td>
                      <td>{row.priority}</td>
                      <td>
                        <span className={STATUS_CLASS[row.status || ''] || 'badge badge-danger'}>
                          {(row.status || '').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>
                        <div>{row.message}</div>
                        {(row.warnings || []).map(warning => (
                          <small className="d-block text-warning" key={warning}>
                            Warning: {warning}
                          </small>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  }
}
